import * as THREE from 'three'
import type { GSplatClipPacket } from '@/plugins/gsplat/clipping/GSplatClipState'
import centersPointsFrag from '@/plugins/gsplat/shaders/chunks/frag/centersPoints.glsl'
import centersPointsVert from '@/plugins/gsplat/shaders/chunks/vert/centersPoints.glsl'

// 类型从 shared 层 re-export
export type { CentersDisplayMode, CentersStyle } from '@/shared/types/gsplat'

import type { CentersStyle } from '@/shared/types/gsplat'

/** 默认中心点样式 */
export const DEFAULT_CENTERS_STYLE: CentersStyle = {
  pointSize: 4.0,
  selectedColor: new THREE.Vector3(1, 1, 0),
  selectedOpacity: 1.0,
  unselectedColor: new THREE.Vector3(0, 0, 1),
  unselectedOpacity: 0.5,
}

function createFallbackStateTexture(): THREE.DataTexture {
  const texture = new THREE.DataTexture(
    new Uint8Array([0]),
    1,
    1,
    THREE.RedFormat,
    THREE.UnsignedByteType
  )
  texture.name = 'fallbackSplatState'
  texture.minFilter = THREE.NearestFilter
  texture.magFilter = THREE.NearestFilter
  texture.generateMipmaps = false
  texture.needsUpdate = true
  return texture
}

/**
 * 中心点渲染材质
 *
 * 独立的 ShaderMaterial，用于将 splat 中心渲染为圆点。
 * 从 WorkBuffer 读取排序后的 splat 位置和状态数据。
 */
export class CentersPointsMaterial extends THREE.ShaderMaterial {
  /** 1x1 全零 R8 纹理；实例所有，dispose 时同步释放。 */
  private _fallbackStateTexture: THREE.DataTexture | null

  constructor() {
    const fallbackStateTexture = createFallbackStateTexture()
    super({
      vertexShader: centersPointsVert,
      fragmentShader: centersPointsFrag,
      glslVersion: THREE.GLSL3,

      uniforms: {
        // 数据源
        transformA: { value: null },
        splatOrder: { value: null },
        numSplats: { value: 0 },
        dataWidth: { value: 0 },
        orderWidth: { value: 0 },

        // 视口和相机
        viewport_size: {
          value: new THREE.Vector4(1920, 1080, 1 / 1920, 1 / 1080),
        },
        camera_params: { value: new THREE.Vector4(0.01, 100, 0.1, 0) },

        // 状态纹理（初始绑定 fallback 全零纹理，避免未绑定纹理采样）
        splatState: { value: fallbackStateTexture },

        // 中心点样式
        pointSize: { value: DEFAULT_CENTERS_STYLE.pointSize },
        selectedColor: { value: DEFAULT_CENTERS_STYLE.selectedColor.clone() },
        selectedOpacity: { value: DEFAULT_CENTERS_STYLE.selectedOpacity },
        unselectedColor: {
          value: DEFAULT_CENTERS_STYLE.unselectedColor.clone(),
        },
        unselectedOpacity: { value: DEFAULT_CENTERS_STYLE.unselectedOpacity },

        // 标量过滤参数
        splatScalar: { value: null },
        scalarFilterEnabled: { value: false },
        scalarFilterRange: { value: new THREE.Vector2(0, 1) },

        // unified LOD polygon 中心点裁切
        gsplatClipData: { value: null },
        gsplatClipPolygonCount: { value: 0 },
      },

      transparent: true,
      depthWrite: false,
      depthTest: true,

      // 预乘 Alpha 混合
      blending: THREE.CustomBlending,
      blendEquation: THREE.AddEquation,
      blendSrc: THREE.OneFactor,
      blendDst: THREE.OneMinusSrcAlphaFactor,
      blendSrcAlpha: THREE.OneFactor,
      blendDstAlpha: THREE.OneMinusSrcAlphaFactor,

      side: THREE.DoubleSide,
    })

    this.name = 'CentersPointsMaterial'
    this._fallbackStateTexture = fallbackStateTexture
  }

  /**
   * 设置 WorkBuffer 纹理和元数据。
   *
   * @param splatTexture0 - splat transformA 数据纹理
   * @param orderTexture - 排序索引纹理
   * @param numSplats - 有效 splat 数量
   * @param textureSize - 纹理边长（像素）
   */
  setWorkBufferData(
    splatTexture0: THREE.DataTexture,
    orderTexture: THREE.DataTexture,
    numSplats: number,
    textureSize: number
  ): void {
    this.uniforms.transformA.value = splatTexture0
    this.uniforms.splatOrder.value = orderTexture
    this.uniforms.numSplats.value = numSplats
    this.uniforms.dataWidth.value = textureSize
    this.uniforms.orderWidth.value = textureSize
  }

  /**
   * 设置视口尺寸。
   *
   * @param width - 视口宽度（像素）
   * @param height - 视口高度（像素）
   */
  setViewport(width: number, height: number): void {
    this.uniforms.viewport_size.value.set(width, height, 1 / width, 1 / height)
  }

  /**
   * 设置相机参数（近远平面与投影类型）。
   *
   * @param camera - 当前相机，支持透视与正交
   */
  setCameraParams(camera: THREE.Camera): void {
    if (camera instanceof THREE.PerspectiveCamera) {
      this.uniforms.camera_params.value.set(1 / camera.far, camera.far, camera.near, 0)
    } else if (camera instanceof THREE.OrthographicCamera) {
      this.uniforms.camera_params.value.set(1 / camera.far, camera.far, camera.near, 1)
    }
  }

  /**
   * 设置 splat 状态纹理。
   *
   * @param texture - 状态纹理；null 时回退到全零 fallback 以确保 texelFetch 采样安全
   */
  setSplatStateTexture(texture: THREE.DataTexture | null): void {
    this.uniforms.splatState.value = texture ?? this._fallbackStateTexture
  }

  /**
   * 更新中心点样式。
   *
   * @param style - 局部样式，仅覆盖提供的字段；数值会被 clamp 到合法范围
   */
  setStyle(style: Partial<CentersStyle>): void {
    if (style.pointSize !== undefined) {
      this.uniforms.pointSize.value = Math.max(1, style.pointSize)
    }
    if (style.selectedColor !== undefined) {
      this.uniforms.selectedColor.value.copy(style.selectedColor)
    }
    if (style.selectedOpacity !== undefined) {
      this.uniforms.selectedOpacity.value = Math.max(0, Math.min(1, style.selectedOpacity))
    }
    if (style.unselectedColor !== undefined) {
      this.uniforms.unselectedColor.value.copy(style.unselectedColor)
    }
    if (style.unselectedOpacity !== undefined) {
      this.uniforms.unselectedOpacity.value = Math.max(0, Math.min(1, style.unselectedOpacity))
    }
  }

  /**
   * 获取当前样式。
   *
   * @returns 样式副本，颜色为克隆的新向量
   */
  getStyle(): CentersStyle {
    return {
      pointSize: this.uniforms.pointSize.value,
      selectedColor: this.uniforms.selectedColor.value.clone(),
      selectedOpacity: this.uniforms.selectedOpacity.value,
      unselectedColor: this.uniforms.unselectedColor.value.clone(),
      unselectedOpacity: this.uniforms.unselectedOpacity.value,
    }
  }

  // ============================================================
  // 标量过滤 API
  // ============================================================

  /**
   * 设置标量纹理。texture 非 null 时添加 HAS_SCALAR_FILTER define，null 时移除。
   *
   * @param texture - 标量纹理；传 null 关闭过滤
   */
  setScalarTexture(texture: THREE.Texture | null): void {
    const hadDefine = this.defines && 'HAS_SCALAR_FILTER' in this.defines
    const needDefine = texture !== null

    this.uniforms.splatScalar.value = texture

    if (needDefine && !hadDefine) {
      this.defines = this.defines || {}
      this.defines.HAS_SCALAR_FILTER = 1
      this.needsUpdate = true
    } else if (!needDefine && hadDefine) {
      this.defines.HAS_SCALAR_FILTER = undefined
      this.needsUpdate = true
    }
  }

  /**
   * 设置标量过滤参数（仅更新 uniforms，不触发 shader 重编译）。
   *
   * @param enabled - 是否启用过滤
   * @param min - 阈值下界
   * @param max - 阈值上界
   */
  setScalarFilter(enabled: boolean, min: number, max: number): void {
    this.uniforms.scalarFilterEnabled.value = enabled
    this.uniforms.scalarFilterRange.value.set(min, max)
  }

  /**
   * 清除标量过滤
   */
  clearScalarFilter(): void {
    this.setScalarTexture(null)
    this.uniforms.scalarFilterEnabled.value = false
    this.uniforms.scalarFilterRange.value.set(0, 1)
  }

  /** 同步 gaussian pass 已编译的裁切 packet，当前材质只借用纹理。 */
  setClipPacket(packet: GSplatClipPacket | null): void {
    const wasEnabled = this.uniforms.gsplatClipData.value !== null
    const isEnabled = packet !== null
    this.uniforms.gsplatClipData.value = packet?.texture ?? null
    this.uniforms.gsplatClipPolygonCount.value = packet
      ? (packet.volumeCount ?? packet.polygonCount)
      : 0

    if (isEnabled) {
      this.defines = this.defines || {}
      this.defines.GSPLAT_CLIP_ENABLED = 1
    } else {
      Reflect.deleteProperty(this.defines, 'GSPLAT_CLIP_ENABLED')
    }

    if (wasEnabled !== isEnabled) {
      this.needsUpdate = true
    }
  }

  /**
   * 释放资源
   */
  dispose(): void {
    this.setClipPacket(null)
    this._fallbackStateTexture?.dispose()
    this._fallbackStateTexture = null
    super.dispose()
    this.uniforms.transformA.value = null
    this.uniforms.splatOrder.value = null
    this.uniforms.splatState.value = null
    this.uniforms.splatScalar.value = null
    this.uniforms.gsplatClipData.value = null
  }
}
